
import React, { useEffect, useRef } from "react";
import { baseUrl } from "configs/app.config";
import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from "utils/deepParseJson";
import { useState } from "react";
import useAuth from "utils/hooks/useAuth";
import { useSelector } from "react-redux";
import { notificationService } from "services/NotificationService";
import { pusherBeamsService } from "services/PusherBeamsService";



export const pusherContext = React.createContext()

// El banner de permisos avisa por acá cuando la persona concede el permiso, para
// que Beams arranque en el momento y no en la próxima carga. Es un evento del
// window y no un estado de Redux porque el banner vive dentro del panel de la
// campanita, lejos de este provider en el árbol.
export const PERMISO_CONCEDIDO = 'storehub:notification-permission-granted'




const PusherProvider = ({ children }) => {

    const { authenticated, setAuthUser } = useAuth()
    const [pusherChannel, setPusherChannel] = useState(null)
    // Un solo reintento de /account/me. Si el backend no devolviera el canal, sin
    // esta bandera el efecto se dispararía en loop contra el servidor.
    const yaPediMeDeNuevo = useRef(false)

    // Obtener tanto el estado de sesión como el usuario
    const { signedIn } = useSelector(state => state.auth.session)
    const user = useSelector(state => state.auth.user)


    const connectPusher = async () => {
        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME)
        const persistData = deepParseJson(rawPersistData)

        // Validar que los datos necesarios estén disponibles
        if (!persistData?.auth?.session?.token) {
            console.error('❌ Token no disponible en localStorage');
            return;
        }

        // El canal sale del estado de Redux, NO de localStorage: redux-persist
        // escribe de forma asíncrona, así que el usuario que disparó este efecto
        // puede tener el canal nuevo mientras localStorage todavía guarda la forma
        // vieja. Ahí el fallback al uuid pelado nos devolvería al canal compartido
        // entre tenants, y como el uuid no cambia, no habría reconexión que lo
        // corrigiera.
        const canal = user?.notificationsChannel

        if (!canal) {
            console.error('❌ Canal de notificaciones no disponible todavía');
            console.log('ℹ️ Esto es normal inmediatamente después del login.');
            console.log('ℹ️ Pusher se inicializará cuando /account/me haya respondido.');
            return;
        }

        const accessToken = persistData.auth.session.token
        const uuid = user.uuid

        console.log('🔑 Usuario autenticado:', uuid);

        const backendBaseUrl = baseUrl;

        // Inicializar Laravel Echo con el NotificationService
        notificationService.initialize(canal, accessToken, backendBaseUrl);

        // Acá NO se pide el permiso. Antes sí, y ese era el bug: el pedido salía
        // solo al entrar, sin que la persona hubiera tocado nada. El navegador
        // muestra ese diálogo una vez; si se descarta, deja de mostrarlo para el
        // origen, y el botón "Activar avisos" del panel pasa a resolver sin abrir
        // ningún diálogo. O sea que el pedido automático rompía justamente al
        // botón que sí tiene un gesto de la persona atrás.
        //
        // El único que pide es NotificationPermissionBanner, con un click real.
        // Acá sólo se lee el estado actual, que no dispara nada.
        console.log(
            notificationService.leerPermisoActual()
                ? '✅ Permisos de notificación ya concedidos'
                : 'ℹ️ Permisos de notificación no concedidos todavía'
        );

        // Obtener el canal PRIMERO (no bloquear por push subscription)
        const channel = notificationService.getChannel();
        setPusherChannel(channel);
        console.log("✅ Pusher conectado vía Laravel Echo");

        arrancarBeams(canal, accessToken);
    }

    // Beams, aparte de la conexión de Echo: hay que poder llamarlo de nuevo
    // cuando la persona concede el permiso, sin rehacer la conexión de Pusher.
    const arrancarBeams = (canal, accessToken) => {
        const beamsInstanceId = process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID;
        const isProduction = process.env.NODE_ENV === 'production';
        const enableInDev = process.env.REACT_APP_PUSHER_BEAMS_ENABLE_DEV === 'true';

        if (!beamsInstanceId) {
            console.log('⚠️ REACT_APP_PUSHER_BEAMS_INSTANCE_ID no configurado en .env');
            return;
        }

        if (!isProduction && !enableInDev) {
            console.log('ℹ️ MODO DESARROLLO: Pusher Beams deshabilitado (REACT_APP_PUSHER_BEAMS_ENABLE_DEV=true para probarlo)');
            return;
        }

        // Beams arranca SOLO con el permiso ya concedido. `beamsClient.start()`
        // pide el permiso de notificaciones por su cuenta, así que arrancarlo
        // siempre reintroducía el prompt sin gesto que este cambio vino a sacar.
        if (!notificationService.leerPermisoActual()) {
            console.log('ℹ️ Pusher Beams en espera: falta el permiso de notificaciones');
            return;
        }

        console.log('🔄 Iniciando Pusher Beams...');

        pusherBeamsService.initialize(beamsInstanceId)
            .then(initialized => {
                if (!initialized) {
                    console.log('⚠️ Pusher Beams no pudo inicializarse');
                    return;
                }

                console.log('✅ Pusher Beams inicializado, registrando dispositivo...');
                // Se registra con el canal, no con el uuid: el uuid se repite
                // entre tenants y el push de Beams llega al sistema operativo
                // aunque el navegador esté cerrado, así que ahí la fuga es peor.
                return pusherBeamsService.start(canal, accessToken);
            })
            .then(registered => {
                if (registered) {
                    console.log('✅ Pusher Beams completamente configurado y listo');
                }
            })
            .catch(error => {
                console.error('❌ ERROR CON PUSHER BEAMS:', error);
                console.log('ℹ️ Las notificaciones funcionarán solo con el navegador abierto');
            });
    }

    useEffect(() => {
        // Se espera al CANAL y no al uuid: es el canal el que decide a qué se
        // suscribe Echo, y llega en la misma respuesta de /account/me.
        if (authenticated && user?.notificationsChannel) {
            console.log('🔄 Iniciando conexión Pusher - Canal disponible:', user.notificationsChannel);
            connectPusher();
        } else if (authenticated && user?.uuid && !yaPediMeDeNuevo.current) {
            // Sesión abierta desde ANTES de este cambio: el usuario persistido en
            // el navegador no tiene el canal, y /account/me sólo se vuelve a
            // llamar al iniciar sesión. Sin esto, todas las sesiones vivas se
            // quedaban sin campanita hasta que la persona cerrara sesión.
            yaPediMeDeNuevo.current = true
            console.log('🔄 Sesión sin canal de notificaciones: refrescando el usuario');
            setAuthUser().catch((error) => {
                console.warn('⚠️ No se pudo refrescar el usuario:', error?.message || error);
            });
        } else if (authenticated) {
            console.log('⏳ Esperando a que el usuario se cargue...');
        }

        // Cuando la persona concede el permiso desde el banner, Beams tiene que
        // arrancar en el momento. Sin esto el cartel decía "avisos activados" y
        // el push recién empezaba a funcionar en la próxima carga de la app.
        const alConcederElPermiso = () => {
            const canal = user?.notificationsChannel
            const persistData = deepParseJson(localStorage.getItem(PERSIST_STORE_NAME))
            const accessToken = persistData?.auth?.session?.token

            if (canal && accessToken) arrancarBeams(canal, accessToken)
        }

        window.addEventListener(PERMISO_CONCEDIDO, alConcederElPermiso)

        // Cleanup al desmontar
        return () => {
            window.removeEventListener(PERMISO_CONCEDIDO, alConcederElPermiso);
            notificationService.disconnect();
            pusherBeamsService.stop();
        }
    // El canal cambia al cambiar de tenant sin cerrar sesión (otro subdominio,
    // mismo uuid), cosa que `user?.uuid` no detectaba: por eso la dependencia es
    // el canal y no el uuid.
    }, [signedIn, user?.uuid, user?.notificationsChannel])

    return (
        <pusherContext.Provider value={pusherChannel}>
            {children}
        </pusherContext.Provider>
    )

}
export default PusherProvider